import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, ChevronDown, ChevronRight, Download, Trash2, CreditCard, Filter, FileText, Paperclip, Pencil } from 'lucide-react';
import VendorSelect from '@/components/VendorSelect';
import AttachmentPreview from '@/components/AttachmentPreview';

const billTypes = ['Vendor Payment', 'Material Purchase', 'Service Fee', 'Labour', 'Misc'];
const paymentModes = ['Bank Transfer', 'UPI', 'Cash', 'Cheque', 'Card', 'Other'];

function formatINR(n) {
  if (!n && n !== 0) return '0';
  return Number(n).toLocaleString('en-IN');
}

function formatDate(d) {
  if (!d) return '-';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
}

function getFinancialState(billAmount, totalPaid) {
  if (totalPaid >= billAmount) return { label: 'Cleared', color: 'bg-[#7DA68A]/10 text-[#7DA68A]' };
  if (totalPaid > 0) return { label: 'Partial', color: 'bg-[#C67D5B]/10 text-[#C67D5B]' };
  return { label: 'Outstanding', color: 'bg-[#C4727F]/10 text-[#C4727F]' };
}

function getTimeState(billDate) {
  if (!billDate) return { label: 'On Track', color: 'bg-[#7DA68A]/10 text-[#7DA68A]' };
  const days = Math.floor((Date.now() - new Date(billDate).getTime()) / 86400000);
  if (days > 30) return { label: 'Overdue', color: 'bg-[#C4727F]/10 text-[#C4727F]' };
  if (days > 20) return { label: 'Due Soon', color: 'bg-[#C67D5B]/10 text-[#C67D5B]' };
  return { label: 'On Track', color: 'bg-[#7DA68A]/10 text-[#7DA68A]' };
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ProjectBilling({ projectId, projectName, billingData, onUpdate }) {
  const [entries, setEntries] = useState(billingData || []);
  const [expanded, setExpanded] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(null); // entry id being edited
  const [showPayment, setShowPayment] = useState(null); // entry id (add mode)
  const [editPayment, setEditPayment] = useState(null); // {entryId, paymentId} (edit mode)
  const [previewAtt, setPreviewAtt] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterVendor, setFilterVendor] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const [sortBy, setSortBy] = useState('adaptive'); // adaptive | date
  const fileRef = useRef(null);
  const formFileRef = useRef(null);
  const [attachEntry, setAttachEntry] = useState(null);
  const [formAttachments, setFormAttachments] = useState([]);

  const [form, setForm] = useState({
    bill_type: 'Vendor Payment', vendor_name: '', bill_number: '', bill_date: '', bill_amount: '', notes: ''
  });
  const [payForm, setPayForm] = useState({ date: '', amount: '', mode: 'Bank Transfer', reference: '', notes: '' });

  useEffect(() => { setEntries(billingData || []); }, [billingData]);

  // Computed values
  const enriched = useMemo(() => entries.map(e => {
    const billAmt = Number(e.bill_amount) || 0;
    const totalPaid = (e.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const pending = Math.max(0, billAmt - totalPaid);
    return { ...e, total_paid: totalPaid, pending_amount: pending, financial_state: getFinancialState(billAmt, totalPaid), time_state: getTimeState(e.financial_state?.label !== 'Cleared' ? e.bill_date : null) };
  }), [entries]);

  // Filters
  const filtered = useMemo(() => {
    let result = enriched;
    if (filterType !== 'all') result = result.filter(e => e.bill_type === filterType);
    if (filterVendor !== 'all') result = result.filter(e => e.vendor_name === filterVendor);
    if (filterState !== 'all') result = result.filter(e => e.financial_state.label === filterState);
    // Sorting
    if (sortBy === 'date') {
      result = [...result].sort((a, b) => (b.bill_date || '').localeCompare(a.bill_date || ''));
    } else {
      // Adaptive: pending desc, then overdue first, then recency
      result = [...result].sort((a, b) => {
        const aOverdue = a.time_state.label === 'Overdue' ? 1 : 0;
        const bOverdue = b.time_state.label === 'Overdue' ? 1 : 0;
        if (bOverdue !== aOverdue) return bOverdue - aOverdue;
        return b.pending_amount - a.pending_amount;
      });
    }
    return result;
  }, [enriched, filterType, filterVendor, filterState, sortBy]);

  // Computation strip
  const totals = useMemo(() => {
    const billed = enriched.reduce((s, e) => s + (Number(e.bill_amount) || 0), 0);
    const paid = enriched.reduce((s, e) => s + e.total_paid, 0);
    const pending = enriched.reduce((s, e) => s + e.pending_amount, 0);
    const atRisk = enriched.filter(e => e.time_state.label === 'Overdue').reduce((s, e) => s + e.pending_amount, 0);
    return { billed, paid, pending, atRisk };
  }, [enriched]);

  const vendors = useMemo(() => [...new Set(entries.map(e => e.vendor_name).filter(Boolean))], [entries]);

  const addEntry = async () => {
    if (!form.vendor_name.trim() || !form.bill_amount) { toast.error('Vendor and amount required'); return; }
    try {
      const res = await api.post(`/projects/${projectId}/billing`, { ...form, bill_amount: Number(form.bill_amount), attachments: formAttachments });
      const updated = [...entries, res.data];
      setEntries(updated);
      onUpdate?.(updated);
      toast.success('Billing entry added');
      setShowAdd(false);
      setForm({ bill_type: 'Vendor Payment', vendor_name: '', bill_number: '', bill_date: '', bill_amount: '', notes: '' });
      setFormAttachments([]);
    } catch { toast.error('Failed to add'); }
  };

  const handleFormFileSelect = async (files) => {
    const accepted = Array.from(files);
    const added = [];
    for (const file of accepted) {
      const dataUrl = await readFileAsDataUrl(file);
      added.push({ id: crypto.randomUUID?.() || Math.random().toString(36).slice(2), name: file.name, size: file.size, data_url: dataUrl });
    }
    setFormAttachments(prev => [...prev, ...added]);
  };

  const openEdit = (entry) => {
    setShowEdit(entry.id);
    setForm({
      bill_type: entry.bill_type || 'Vendor Payment',
      vendor_name: entry.vendor_name || '',
      bill_number: entry.bill_number || '',
      bill_date: entry.bill_date || '',
      bill_amount: entry.bill_amount || '',
      notes: entry.notes || ''
    });
  };

  const saveEdit = async () => {
    if (!showEdit) return;
    if (!form.vendor_name.trim() || !form.bill_amount) { toast.error('Vendor and amount required'); return; }
    try {
      await api.put(`/projects/${projectId}/billing/${showEdit}`, { ...form, bill_amount: Number(form.bill_amount) });
      const updated = entries.map(e => e.id === showEdit ? { ...e, ...form, bill_amount: Number(form.bill_amount) } : e);
      setEntries(updated);
      onUpdate?.(updated);
      toast.success('Billing entry updated');
      setShowEdit(null);
      setForm({ bill_type: 'Vendor Payment', vendor_name: '', bill_number: '', bill_date: '', bill_amount: '', notes: '' });
    } catch { toast.error('Update failed'); }
  };

  const deleteEntry = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/projects/${projectId}/billing/${deleteTarget}`);
      const updated = entries.filter(e => e.id !== deleteTarget);
      setEntries(updated);
      onUpdate?.(updated);
      toast.success('Entry deleted');
    } catch { toast.error('Delete failed'); }
    setDeleteTarget(null);
  };

  const addPayment = async () => {
    if (!showPayment || !payForm.amount) { toast.error('Amount required'); return; }
    try {
      const res = await api.post(`/projects/${projectId}/billing/${showPayment}/payments`, { ...payForm, amount: Number(payForm.amount) });
      const updated = entries.map(e => e.id === showPayment ? { ...e, payments: [...(e.payments || []), res.data] } : e);
      setEntries(updated);
      onUpdate?.(updated);
      toast.success('Payment recorded');
      setShowPayment(null);
      setPayForm({ date: '', amount: '', mode: 'Bank Transfer', reference: '', notes: '' });
    } catch { toast.error('Failed'); }
  };

  const openEditPayment = (entryId, payment) => {
    setEditPayment({ entryId, paymentId: payment.id });
    setPayForm({
      date: payment.date || '',
      amount: payment.amount != null ? String(payment.amount) : '',
      mode: payment.mode || 'Bank Transfer',
      reference: payment.reference || '',
      notes: payment.notes || ''
    });
  };

  const saveEditPayment = async () => {
    if (!editPayment || !payForm.amount) { toast.error('Amount required'); return; }
    try {
      await api.put(
        `/projects/${projectId}/billing/${editPayment.entryId}/payments/${editPayment.paymentId}`,
        { ...payForm, amount: Number(payForm.amount) }
      );
      const updated = entries.map(e => e.id === editPayment.entryId ? {
        ...e,
        payments: (e.payments || []).map(p => p.id === editPayment.paymentId ? { ...p, ...payForm, amount: Number(payForm.amount) } : p)
      } : e);
      setEntries(updated);
      onUpdate?.(updated);
      toast.success('Payment updated');
      setEditPayment(null);
      setPayForm({ date: '', amount: '', mode: 'Bank Transfer', reference: '', notes: '' });
    } catch { toast.error('Failed to update payment'); }
  };

  const deletePayment = async (entryId, paymentId) => {
    try {
      await api.delete(`/projects/${projectId}/billing/${entryId}/payments/${paymentId}`);
      const updated = entries.map(e => e.id === entryId ? { ...e, payments: (e.payments || []).filter(p => p.id !== paymentId) } : e);
      setEntries(updated);
      onUpdate?.(updated);
      toast.success('Payment removed');
    } catch { toast.error('Failed'); }
  };

  const handleAttach = async (files) => {
    if (!attachEntry) return;
    const accepted = Array.from(files);
    for (const file of accepted) {
      const dataUrl = await readFileAsDataUrl(file);
      const attachment = { id: crypto.randomUUID?.() || Math.random().toString(36).slice(2), name: file.name, size: file.size, data_url: dataUrl };
      const entry = entries.find(e => e.id === attachEntry);
      const updatedAttachments = [...(entry?.attachments || []), attachment];
      await api.put(`/projects/${projectId}/billing/${attachEntry}`, { attachments: updatedAttachments });
      const updated = entries.map(e => e.id === attachEntry ? { ...e, attachments: updatedAttachments } : e);
      setEntries(updated);
      onUpdate?.(updated);
    }
    toast.success('File attached');
    setAttachEntry(null);
  };

  const exportExcel = () => {
    import('xlsx').then(XLSX => {
      import('file-saver').then(({ saveAs }) => {
        const data = enriched.map(e => ({
          'Bill Type': e.bill_type, 'Vendor': e.vendor_name, 'Bill No.': e.bill_number,
          'Bill Date': e.bill_date, 'Bill Amount': e.bill_amount, 'Total Paid': e.total_paid,
          'Pending': e.pending_amount, 'Status': e.financial_state.label, 'Time': e.time_state.label, 'Notes': e.notes
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Billing');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf]), `Billing-${projectName || 'Project'}.xlsx`);
        toast.success('Exported');
      });
    });
  };

  return (
    <div className="space-y-4" data-testid="project-billing">
      {/* Computation Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="billing-computation-strip">
        <div className="glass-card rounded-xl p-4">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Total Billed</p>
          <p className="text-lg font-bold text-[#2C2C2E] font-['Manrope'] mt-1">Rs {formatINR(totals.billed)}</p>
        </div>
        <div className="glass-card rounded-xl p-4">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Total Paid</p>
          <p className="text-lg font-bold text-[#7DA68A] font-['Manrope'] mt-1">Rs {formatINR(totals.paid)}</p>
        </div>
        <div className="glass-card rounded-xl p-4">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Total Pending</p>
          <p className="text-lg font-bold text-[#C67D5B] font-['Manrope'] mt-1">Rs {formatINR(totals.pending)}</p>
        </div>
        <div className="glass-card rounded-xl p-4">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">At-Risk (Overdue)</p>
          <p className="text-lg font-bold text-[#C4727F] font-['Manrope'] mt-1">Rs {formatINR(totals.atRisk)}</p>
        </div>
      </div>

      {/* Header + Filters */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={14} strokeWidth={1.5} className="text-[#6B7280]" />
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="h-8 w-32 rounded-lg border-[#E8E6E1] text-[11px]"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {billTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterVendor} onValueChange={setFilterVendor}>
            <SelectTrigger className="h-8 w-32 rounded-lg border-[#E8E6E1] text-[11px]"><SelectValue placeholder="Vendor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Vendors</SelectItem>
              {vendors.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterState} onValueChange={setFilterState}>
            <SelectTrigger className="h-8 w-28 rounded-lg border-[#E8E6E1] text-[11px]"><SelectValue placeholder="State" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              <SelectItem value="Outstanding">Outstanding</SelectItem>
              <SelectItem value="Partial">Partial</SelectItem>
              <SelectItem value="Cleared">Cleared</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-8 w-28 rounded-lg border-[#E8E6E1] text-[11px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="adaptive">Adaptive</SelectItem>
              <SelectItem value="date">By Date</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <button onClick={exportExcel} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8E6E1] text-[#2C2C2E] text-xs font-medium hover:bg-[#D4D0C8] transition-colors" data-testid="billing-export">
            <Download size={13} /> Export
          </button>
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors" data-testid="billing-add-entry">
            <Plus size={14} /> Add Entry
          </button>
        </div>
      </div>

      {/* Stateful Grid */}
      <div className="space-y-2" data-testid="billing-grid">
        {filtered.map(entry => {
          const isExpanded = expanded[entry.id];
          return (
            <div key={entry.id} className="glass-card rounded-xl overflow-hidden" data-testid={`billing-entry-${entry.id}`}>
              {/* Row */}
              <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#FAFAF8]/50 transition-colors group"
                onClick={() => setExpanded(prev => ({ ...prev, [entry.id]: !prev[entry.id] }))}>
                <button className="shrink-0">
                  {isExpanded ? <ChevronDown size={14} className="text-[#6B7280]" /> : <ChevronRight size={14} className="text-[#6B7280]" />}
                </button>
                <div className="flex-1 min-w-0 grid grid-cols-6 gap-3 items-center">
                  <div className="col-span-2 min-w-0">
                    <p className="text-sm font-medium text-[#2C2C2E] truncate">{entry.vendor_name || 'Unknown Vendor'}</p>
                    <p className="text-[10px] text-[#6B7280]">{entry.bill_type} {entry.bill_number ? `· #${entry.bill_number}` : ''}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-[#2C2C2E]">Rs {formatINR(entry.bill_amount)}</p>
                    <p className="text-[9px] text-[#6B7280]">Billed</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-[#7DA68A]">Rs {formatINR(entry.total_paid)}</p>
                    <p className="text-[9px] text-[#6B7280]">Paid</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-semibold ${entry.pending_amount > 0 ? 'text-[#C67D5B]' : 'text-[#7DA68A]'}`}>
                      Rs {formatINR(entry.pending_amount)}
                    </p>
                    <p className="text-[9px] text-[#6B7280]">Pending</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={`text-[8px] ${entry.financial_state.color}`}>{entry.financial_state.label}</Badge>
                    {entry.pending_amount > 0 && <Badge className={`text-[8px] ${entry.time_state.color}`}>{entry.time_state.label}</Badge>}
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={e => { e.stopPropagation(); setShowPayment(entry.id); setPayForm({ date: new Date().toISOString().slice(0, 10), amount: '', mode: 'Bank Transfer', reference: '', notes: '' }); }}
                    className="p-1.5 rounded-lg hover:bg-[#3A8C8C]/10 transition-colors" title="Add Payment" data-testid={`billing-add-payment-${entry.id}`}>
                    <CreditCard size={13} className="text-[#3A8C8C]" />
                  </button>
                  <button onClick={e => { e.stopPropagation(); openEdit(entry); }}
                    className="p-1.5 rounded-lg hover:bg-[#3A8C8C]/10 transition-colors" title="Edit Bill" data-testid={`billing-edit-${entry.id}`}>
                    <Pencil size={13} className="text-[#6B7280]" />
                  </button>
                  <button onClick={e => { e.stopPropagation(); setAttachEntry(entry.id); fileRef.current?.click(); }}
                    className="p-1.5 rounded-lg hover:bg-[#3A8C8C]/10 transition-colors" title="Attach" data-testid={`billing-attach-${entry.id}`}>
                    <Paperclip size={13} className="text-[#6B7280]" />
                  </button>
                  <button onClick={e => { e.stopPropagation(); setDeleteTarget(entry.id); }}
                    className="p-1.5 rounded-lg hover:bg-[#C4727F]/10 transition-colors" title="Delete" data-testid={`billing-delete-${entry.id}`}>
                    <Trash2 size={13} className="text-[#C4727F]" />
                  </button>
                </div>
              </div>

              {/* Expansion Panel */}
              {isExpanded && (
                <div className="border-t border-[#E8E6E1] bg-[#FAFAF8]/30 px-4 py-4 space-y-4" data-testid={`billing-expanded-${entry.id}`}>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div><span className="text-[#6B7280]">Bill Date:</span> <span className="font-medium text-[#2C2C2E] ml-1">{formatDate(entry.bill_date)}</span></div>
                    <div><span className="text-[#6B7280]">Bill No:</span> <span className="font-medium text-[#2C2C2E] ml-1">{entry.bill_number || '-'}</span></div>
                    <div><span className="text-[#6B7280]">Type:</span> <span className="font-medium text-[#2C2C2E] ml-1">{entry.bill_type}</span></div>
                    <div><span className="text-[#6B7280]">Notes:</span> <span className="font-medium text-[#2C2C2E] ml-1">{entry.notes || '-'}</span></div>
                  </div>

                  {/* Payment Timeline */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">Payment Timeline</h5>
                      <button onClick={() => { setShowPayment(entry.id); setPayForm({ date: new Date().toISOString().slice(0, 10), amount: String(entry.pending_amount), mode: 'Bank Transfer', reference: '', notes: '' }); }}
                        className="text-[10px] text-[#3A8C8C] hover:underline flex items-center gap-1" data-testid={`billing-add-payment-inline-${entry.id}`}>
                        <Plus size={10} /> Add Payment
                      </button>
                    </div>
                    {(entry.payments || []).length > 0 ? (
                      <div className="space-y-1.5">
                        {(entry.payments || []).map(p => (
                          <div key={p.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white border border-[#E8E6E1] group/pay">
                            <div className="flex items-center gap-3">
                              <div className="w-2 h-2 rounded-full bg-[#7DA68A]" />
                              <span className="text-xs text-[#6B7280]">{formatDate(p.date)}</span>
                              <span className="text-xs font-semibold text-[#2C2C2E]">Rs {formatINR(p.amount)}</span>
                              <Badge className="bg-[#E8E6E1] text-[#2C2C2E] text-[8px]">{p.mode}</Badge>
                              {p.reference && <span className="text-[9px] text-[#6B7280]">Ref: {p.reference}</span>}
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover/pay:opacity-100 transition-all">
                              <button onClick={() => openEditPayment(entry.id, p)}
                                className="p-1 rounded hover:bg-[#3A8C8C]/10" title="Edit Payment" data-testid={`payment-edit-${p.id}`}>
                                <Pencil size={11} className="text-[#3A8C8C]" />
                              </button>
                              <button onClick={() => deletePayment(entry.id, p.id)}
                                className="p-1 rounded hover:bg-[#C4727F]/10" title="Delete Payment" data-testid={`payment-delete-${p.id}`}>
                                <Trash2 size={11} className="text-[#C4727F]" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[10px] text-[#6B7280] py-2">No payments recorded yet</p>
                    )}
                  </div>

                  {/* Attachments */}
                  {(entry.attachments || []).length > 0 && (
                    <div>
                      <h5 className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider mb-2">Attachments</h5>
                      <div className="flex flex-wrap gap-2">
                        {(entry.attachments || []).map(a => (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => setPreviewAtt(a)}
                            className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white border border-[#E8E6E1] hover:border-[#3A8C8C] hover:bg-[#3A8C8C]/5 transition-colors"
                            data-testid={`billing-attachment-${a.id}`}
                            title="Click to preview"
                          >
                            <FileText size={11} className="text-[#3A8C8C]" />
                            <span className="text-[10px] text-[#2C2C2E]">{a.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-12">
            <CreditCard size={32} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-3" />
            <p className="text-sm text-[#6B7280]">No billing entries yet</p>
          </div>
        )}
      </div>

      {/* Hidden file input for attachments */}
      <input ref={fileRef} type="file" className="hidden" onChange={e => { handleAttach(e.target.files); e.target.value = ''; }} />

      {/* Add Entry Dialog */}
      <Dialog open={showAdd} onOpenChange={(o) => { setShowAdd(o); if (!o) setFormAttachments([]); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Manrope']">Add Billing Entry</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#6B7280] uppercase">Bill Type</Label>
                <Select value={form.bill_type} onValueChange={v => setForm({ ...form, bill_type: v })}>
                  <SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]"><SelectValue /></SelectTrigger>
                  <SelectContent>{billTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Vendor / Party *</Label>
                <div className="mt-1">
                  <VendorSelect
                    value={form.vendor_name}
                    onChange={(name) => setForm({ ...form, vendor_name: name })}
                    testId="billing-form-vendor"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#6B7280] uppercase">Bill Number</Label>
                <Input value={form.bill_number} onChange={e => setForm({ ...form, bill_number: e.target.value })}
                  className="mt-1 h-10 rounded-xl border-[#D4D0C8]" placeholder="INV-001" /></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Bill Date</Label>
                <Input type="date" value={form.bill_date} onChange={e => setForm({ ...form, bill_date: e.target.value })}
                  className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
            </div>
            <div><Label className="text-xs text-[#6B7280] uppercase">Bill Amount (Rs)</Label>
              <Input type="number" value={form.bill_amount} onChange={e => setForm({ ...form, bill_amount: e.target.value })}
                className="mt-1 h-10 rounded-xl border-[#D4D0C8]" placeholder="0" data-testid="billing-form-amount" /></div>
            <div><Label className="text-xs text-[#6B7280] uppercase">Notes</Label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                className="mt-1 w-full h-16 px-4 py-2 bg-white border border-[#D4D0C8] rounded-xl text-sm resize-none" /></div>
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Photo / Bill Image</Label>
              <input ref={formFileRef} type="file" accept="image/*,.pdf" multiple className="hidden"
                onChange={e => { handleFormFileSelect(e.target.files); e.target.value = ''; }} data-testid="billing-form-file-input" />
              <button type="button" onClick={() => formFileRef.current?.click()}
                className="mt-1 w-full flex items-center justify-center gap-2 h-10 rounded-xl border border-dashed border-[#D4D0C8] text-sm text-[#6B7280] hover:border-[#3A8C8C] hover:text-[#3A8C8C] hover:bg-[#3A8C8C]/5 transition-colors"
                data-testid="billing-form-attach-photo">
                <Paperclip size={14} /> Attach photo / file
              </button>
              {formAttachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="billing-form-attachments">
                  {formAttachments.map(a => (
                    <div key={a.id} className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-[#FAFAF8] border border-[#E8E6E1]">
                      {a.data_url?.startsWith('data:image') ? (
                        <img src={a.data_url} alt={a.name} className="w-6 h-6 rounded object-cover" />
                      ) : <FileText size={11} className="text-[#3A8C8C]" />}
                      <span className="text-[10px] text-[#2C2C2E] max-w-[120px] truncate">{a.name}</span>
                      <button type="button" onClick={() => setFormAttachments(prev => prev.filter(x => x.id !== a.id))}
                        className="text-[#C4727F] hover:text-[#B5616E]" data-testid={`billing-form-remove-att-${a.id}`}>
                        <Trash2 size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button onClick={addEntry} className="w-full h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors" data-testid="billing-form-submit">
              Add Entry
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={!!showEdit} onOpenChange={(o) => { if (!o) setShowEdit(null); }}>
        <DialogContent className="max-w-md" data-testid="billing-edit-dialog">
          <DialogHeader><DialogTitle className="font-['Manrope']">Edit Billing Entry</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#6B7280] uppercase">Bill Type</Label>
                <Select value={form.bill_type} onValueChange={v => setForm({ ...form, bill_type: v })}>
                  <SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]"><SelectValue /></SelectTrigger>
                  <SelectContent>{billTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Vendor / Party *</Label>
                <div className="mt-1">
                  <VendorSelect
                    value={form.vendor_name}
                    onChange={(name) => setForm({ ...form, vendor_name: name })}
                    testId="billing-edit-vendor"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#6B7280] uppercase">Bill Number</Label>
                <Input value={form.bill_number} onChange={e => setForm({ ...form, bill_number: e.target.value })}
                  className="mt-1 h-10 rounded-xl border-[#D4D0C8]" placeholder="INV-001" /></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Bill Date</Label>
                <Input type="date" value={form.bill_date} onChange={e => setForm({ ...form, bill_date: e.target.value })}
                  className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
            </div>
            <div><Label className="text-xs text-[#6B7280] uppercase">Bill Amount (Rs)</Label>
              <Input type="number" value={form.bill_amount} onChange={e => setForm({ ...form, bill_amount: e.target.value })}
                className="mt-1 h-10 rounded-xl border-[#D4D0C8]" placeholder="0" data-testid="billing-edit-amount" /></div>
            <div><Label className="text-xs text-[#6B7280] uppercase">Notes</Label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                className="mt-1 w-full h-16 px-4 py-2 bg-white border border-[#D4D0C8] rounded-xl text-sm resize-none" /></div>
            <div className="flex gap-2">
              <button onClick={() => setShowEdit(null)} className="flex-1 h-10 rounded-full border border-[#D4D0C8] text-sm font-medium text-[#6B7280] hover:bg-[#E8E6E1] transition-colors">Cancel</button>
              <button onClick={saveEdit} className="flex-1 h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors" data-testid="billing-edit-submit">Save Changes</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Payment Dialog */}
      <Dialog open={!!showPayment || !!editPayment} onOpenChange={(o) => { if (!o) { setShowPayment(null); setEditPayment(null); } }}>
        <DialogContent className="max-w-sm" data-testid={editPayment ? 'payment-edit-dialog' : 'payment-add-dialog'}>
          <DialogHeader><DialogTitle className="font-['Manrope']">{editPayment ? 'Edit Payment' : 'Record Payment'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#6B7280] uppercase">Date</Label>
                <Input type="date" value={payForm.date} onChange={e => setPayForm({ ...payForm, date: e.target.value })}
                  className="mt-1 h-9 rounded-xl border-[#D4D0C8]" data-testid="payment-form-date" /></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Amount (Rs)</Label>
                <Input type="number" value={payForm.amount} onChange={e => setPayForm({ ...payForm, amount: e.target.value })}
                  className="mt-1 h-9 rounded-xl border-[#D4D0C8]" data-testid="payment-form-amount" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#6B7280] uppercase">Mode</Label>
                <Select value={payForm.mode} onValueChange={v => setPayForm({ ...payForm, mode: v })}>
                  <SelectTrigger className="mt-1 h-9 rounded-xl border-[#D4D0C8]" data-testid="payment-form-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>{paymentModes.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Reference No.</Label>
                <Input value={payForm.reference} onChange={e => setPayForm({ ...payForm, reference: e.target.value })}
                  className="mt-1 h-9 rounded-xl border-[#D4D0C8]" data-testid="payment-form-reference" /></div>
            </div>
            <div><Label className="text-xs text-[#6B7280] uppercase">Notes</Label>
              <Input value={payForm.notes} onChange={e => setPayForm({ ...payForm, notes: e.target.value })}
                className="mt-1 h-9 rounded-xl border-[#D4D0C8]" placeholder="Optional" data-testid="payment-form-notes" /></div>
            {editPayment ? (
              <div className="flex gap-2">
                <button onClick={() => setEditPayment(null)} className="flex-1 h-9 rounded-full border border-[#D4D0C8] text-sm font-medium text-[#6B7280] hover:bg-[#E8E6E1] transition-colors">Cancel</button>
                <button onClick={saveEditPayment} className="flex-1 h-9 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors" data-testid="payment-edit-submit">Save Changes</button>
              </div>
            ) : (
              <button onClick={addPayment} className="w-full h-9 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors" data-testid="payment-form-submit">
                Record Payment
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-['Manrope'] text-base">Delete Entry?</DialogTitle></DialogHeader>
          <p className="text-xs text-[#6B7280] mt-1">This billing entry and all its payment records will be permanently deleted.</p>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setDeleteTarget(null)} className="flex-1 h-9 rounded-lg border border-[#D4D0C8] text-xs font-medium text-[#6B7280] hover:bg-[#E8E6E1] transition-colors">Cancel</button>
            <button onClick={deleteEntry} className="flex-1 h-9 rounded-lg bg-[#C4727F] text-white text-xs font-medium hover:bg-[#B5616E] transition-colors" data-testid="billing-delete-confirm">Delete</button>
          </div>
        </DialogContent>
      </Dialog>

      <AttachmentPreview attachment={previewAtt} onClose={() => setPreviewAtt(null)} />
    </div>
  );
}
